<template>
    <form-modal
        ref="formModal"
        :title="i18nTitleModal"
        :in-editing="isEdit"
        size="xl"
        :handle-close="handleCancel"
    >
        <template v-slot:form>
            <form @submit.prevent="handleOk">
                <confirmation-form-modal
                    ref="confirmationFormModal"
                    :title="i18nWorkSchedule"
                    :operation="'assume'"
                />
                <feedback-modal
                    ref="feedbackModal"
                    :title="i18nWorkSchedule"
                />

                <!-- Mensagem informativa -->
                <b-alert variant="info" show class="mb-4">
                    <i class="fas fa-info-circle mr-2"></i>
                    {{ getI18n('COMMON', 'assume_schedule_info') }}
                </b-alert>

                <!-- Campo de pesquisa -->
                <b-row class="mb-4">
                    <b-col md="12">
                        <b-form-group
                            :label="getI18n('COMMON', 'search_user')"
                            class="label-pdv"
                            label-for="search-user"
                        >
                            <b-form-input
                                id="search-user"
                                v-model="searchQuery"
                                class="input-pdv-blue"
                                type="text"
                                :placeholder="getI18n('COMMON', 'search_user')"
                                @input="filterUsers"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <!-- Tabela com usuários disponíveis -->
                <b-row>
                    <b-col md="12">
                        <b-table
                            class="table-pdv"
                            :items="filteredUsers"
                            :fields="tableFields"
                            :busy="loading"
                            striped
                            hover
                            responsive
                            show-empty
                            :empty-text="getI18n('COMMON', 'assume_schedule_empty')"
                            @row-clicked="selectUser"
                        >
                            <template v-slot:table-busy>
                                <div class="text-center text-primary my-2">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <strong> Carregando...</strong>
                                </div>
                            </template>

                            <template v-slot:cell(selected)="row">
                                <b-form-radio
                                    v-model="selectedUserId"
                                    :value="row.item.id"
                                    @change="selectUser(row.item)"
                                ></b-form-radio>
                            </template>

                            <template v-slot:cell(name)="row">
                                <strong>{{ row.item.name }}</strong>
                            </template>

                            <template v-slot:cell(stores_count)="row">
                                <span class="">{{ row.item.stores_count || 0 }}</span>
                            </template>

                            <template v-slot:cell(visits_count)="row">
                                <span class="">{{ row.item.visits_count || 0 }}</span>
                            </template>
                        </b-table>

                        <!-- Paginação -->
                        <b-pagination
                            v-if="totalRows > perPage"
                            :value="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            align="center"
                            class="mt-3"
                            @change="onPageChange"
                        ></b-pagination>
                    </b-col>
                </b-row>
            </form>
        </template>
        <template v-slot:footer>
            <div class="d-flex justify-content-center">
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-4 float-left"
                    :disabled="submitLoading"
                    @onClick="handleCancel"
                />
                <save-button
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :busy="submitLoading"
                    :disabled="!selectedUserId"
                    @onClick="handleOk"
                />
            </div>
        </template>
    </form-modal>
</template>

<script>
    import { numeric, required } from 'vuelidate/lib/validators'
    import WorkScheduleAssumeService from '@src/services/WorkScheduleAssumeService'
    import formModal from '@/src/components/FormModal.vue'
    import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
    import FeedbackModal from '@src/components/FeedbackModal'
    import workScheduleFrequencies from '@src/mixins/workScheduleFrequencies'
    import swalFeedback from '@src/mixins/swalFeedback.vue'
    import validation from '@src/mixins/validation.vue'
    import CancelButton from '@/src/components/CancelButton'
    import SaveButton from '@/src/components/SaveButton'
    import priorityTypes from '@src/mixins/priorityTypes.vue'
    import plannedTypes from '@src/mixins/plannedTypes.vue'

    const i18nKey = 'WORKSCHEDULES'
    const i18nCommon = 'COMMON'

    export default {
        components: {
            formModal,
            ConfirmationFormModal,
            FeedbackModal,
            CancelButton,
            SaveButton,
        },
        mixins: [
            validation,
            swalFeedback,
            workScheduleFrequencies,
            priorityTypes,
            plannedTypes,
        ],
        props: {
            modal: {
                type: Object,
                default: () => {
                    return {
                        id: null,
                        pos_id: null,
                        pos: {
                            id: 0,
                            name: '',
                        },
                        user_id: null,
                        user: {
                            id: 0,
                            name: '',
                        },
                        mix_id: null,
                        mix: {
                            id: 0,
                            name: '',
                            products: [],
                        },
                        start_date: null,
                        end_date: null,
                        human_readable_frequencies: [],
                    }
                },
            },
            modalIsEdit: {
                required: true,
                type: Boolean,
            },
        },
        data() {
            return {
                i18nKey,
                i18nCommon,
                modalWorkSchedule: this.modal,
                isEdit: false,
                submitLoading: false,
                deleteLoading: false,
                loading: false,
                searchQuery: '',
                selectedUserId: null,
                selectedUser: null,
                availableUsers: [],
                filteredUsers: [],
                currentPage: 1,
                perPage: 10,
                totalRows: 0,
                tableFields: [
                    {
                        key: 'selected',
                        label: 'Selecionar',
                        sortable: false,
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    },
                    {
                        key: 'name',
                        label: this.getI18n('USERS', 'TITLES.user'),
                        sortable: true,
                        thClass: 'text-left'
                    },
                    {
                        key: 'stores_count',
                        label: this.getI18n('COMMON', 'store_routed'),
                        sortable: true,
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    },
                    {
                        key: 'visits_count',
                        label: this.getI18n('COMMON', 'visits_routed'),
                        sortable: true,
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    }
                ],
                sourceUserId: null, // ID do usuário da página que está assumindo (recebe) o roteiro
            }
        },
        validations: {
            selectedUserId: {
                required,
                numeric,
            },
        },
        computed: {
            i18nTitleModal() {
                return this.getI18n(i18nKey, 'BUTTONS.assume_schedule')
            },
            i18nWorkSchedule() {
                return this.getI18n(i18nKey, 'TITLES.work_schedule')
            },
            modalOperation() {
                return this.getI18n(i18nKey, 'OPERATIONS.assumed')
            },
        },
        methods: {
            async showModal(sourceUser) {
                this.isEdit = this.modalIsEdit
                this.submitLoading = false
                this.deleteLoading = false
                this.loading = true
                this.sourceUserId = sourceUser.id
                this.selectedUserId = null
                this.selectedUser = null
                this.searchQuery = ''
                this.currentPage = 1

                // Carregar usuários disponíveis para transferência
                await this.loadAvailableUsers()

                this.$refs.formModal.show()
                this.resetValidation()
            },

            async loadAvailableUsers() {
                if (!this.sourceUserId) {
                    return
                }

                this.loading = true
                try {
                    const filters = {
                        search: this.searchQuery,
                        page: this.currentPage,
                        per_page: this.perPage,
                    }

                    const response = await WorkScheduleAssumeService.getAvailableUsers(
                        this.sourceUserId,
                        filters
                    )
                    const { users, total } = WorkScheduleAssumeService.parseAvailableUsersResponse(response)

                    this.availableUsers = users
                    this.filteredUsers = [...users]
                    this.totalRows = total
                } catch (error) {
                    console.error('Erro ao carregar usuários disponíveis:', error)
                    this.availableUsers = []
                    this.filteredUsers = []
                    this.totalRows = 0
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        error?.data?.message || this.getI18n('COMMON', 'assume_schedule_load_failed')
                    )
                } finally {
                    this.loading = false
                }
            },

            async filterUsers() {
                this.currentPage = 1
                await this.loadAvailableUsers()
            },

            async onPageChange(page) {
                this.currentPage = page
                await this.loadAvailableUsers()
            },

            selectUser(user) {
                this.selectedUserId = user.id
                this.selectedUser = user
                this.$v.selectedUserId.$touch()
            },

            async validateTransfer() {
                try {
                    // from = inativo/afastado selecionado; to = usuário da página que assume
                    const response = await WorkScheduleAssumeService.validateTransfer(
                        this.selectedUserId,
                        this.sourceUserId
                    )

                    return WorkScheduleAssumeService.parseValidateResponse(response)
                } catch (error) {
                    console.error('Erro ao validar transferência:', error)

                    return {
                        valid: false,
                        message: error?.data?.message || this.getI18n('ERROR_CODES', 'transfer_failed'),
                    }
                }
            },

            cleanModal() {
                this.selectedUserId = null
                this.selectedUser = null
                this.availableUsers = []
                this.filteredUsers = []
                this.searchQuery = ''
                this.currentPage = 1
                this.totalRows = 0
                this.sourceUserId = null
                this.$v.selectedUserId.$reset()
            },
            handleCancel() {
                this.handleClose(true)
            },
            handleClose(close) {
                if (!close) {
                    return;
                }
                this.resetValidation()
                this.$refs.formModal.hide()
            },
            resetValidation() {
                this.$nextTick(() => this.$v.$reset())
                this.clearAllResponseError()
            },
            handleOk() {
                this.$v.selectedUserId.$touch()
                if (!this.$v.selectedUserId.$invalid && !this.submitLoading) {
                    this.$refs.confirmationFormModal.showModal(false, this.handleSubmit)
                }
            },
            async handleSubmit(submit) {
                if (!submit) {
                    return;
                }

                this.submitLoading = true

                try {
                    // Validar transferência antes de executar
                    const validation = await this.validateTransfer()
                    if (!validation.valid) {
                        this.negativeFeedback(
                            this.getI18n('RESPONSES', 'something_wrong'),
                            validation.message || this.getI18n('ERROR_CODES', 'transfer_failed')
                        )
                        this.submitLoading = false
                        this.selectedUserId = null
                        this.selectedUser = null
                        return
                    }

                    const transferData = {
                        from_user_id: this.selectedUserId,
                        to_user_id: this.sourceUserId,
                    }

                    const response = await WorkScheduleAssumeService.transferSchedule(transferData)
                        .then((response) => response)
                        .catch((error) => error)

                    const statusCode = response.status.toString()

                    if (statusCode.charAt(0) === '2') {
                        this.$emit('refresh')
                        this.$refs.formModal.hide()
                        this.resetValidation()
                        this.positiveFeedback(
                            this.i18nWorkSchedule,
                            this.modalOperation,
                            '3'
                        )
                    } else if (statusCode === '422') {
                        this.modalResponseErrors = response.data.errors
                        this.negativeFeedback(
                            this.getI18n('RESPONSES', 'something_wrong'),
                            response.data.message || this.getI18n('ERROR_CODES', 'transfer_failed')
                        )
                    } else {
                        this.negativeFeedback(
                            this.getI18n('RESPONSES', 'something_wrong'),
                            response.data.message || this.getI18n('ERROR_CODES', 'transfer_failed')
                        )
                    }
                } catch (error) {
                    console.error('Erro ao transferir roteiro:', error)
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        this.getI18n('ERROR_CODES', 'transfer_failed')
                    )
                }

                this.submitLoading = false
            },
        },
    }
</script>
